// @givmo/board — board-roster decode + display helpers.
//
// The roster is the organization's own record of who served and when, so the decode's job is to render what
// the backend recorded and NOTHING beyond it. Three places that matters here:
//
//   * A seat's `status` is `active` or `removed` — never a delete, because who sat on this board is
//     part of the corporate record and a former director's acts must keep resolving to a named seat.
//   * A person's two facts — director or not, and their titles (CBG-YR3-ROLES layer 1) — authorize
//     nothing: every seat resolves to the same principal on the backend, and the UI must not imply
//     otherwise — no "Chair" badge that reads like a permission. They are read and named in one place,
//     `officer-roles.ts`; the legacy `board_role` is read there only for an answer without them.
//   * `seat_provenance` distinguishes a seat this module watched being removed (`module`) from one the
//     organization entered as history (`imported`). They are otherwise indistinguishable, and `removed_at`
//     means different things in each — "when they left" versus "when we were told". `service_history`
//     carries the reading that renders both clocks, so we display THAT rather than inferring.

import { extractList } from '@givmo/api-client/list';
import { displayDate, humanize } from '../../lib/display';
export { displayDate } from '../../lib/display';
import type { OrgBoardMemberOut } from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';

/** The Badge's tone union (the design-system doesn't re-export the tone type directly). */
type BadgeTone = NonNullable<BadgeProps['tone']>;

/**
 * Seats from `GET /board/{org_ref}/members`. That handler is annotated `-> dict`,
 * so the wire is the canonical `{data,meta,links}` envelope which the api-client middleware unwraps to
 * a bare array at runtime — `extractList` reads the bare array, an un-unwrapped envelope, or `[]`, and
 * never throws.
 */
export function extractBoardMembers(body: unknown): OrgBoardMemberOut[] {
  return extractList<OrgBoardMemberOut>(body, 'data');
}

/**
 * A seat for someone who isn't on the board (CBG-YR6-OUTSIDE): its scope is its committees, and it is
 * never a director, holds no title and has no term. Any other value, or none (an older backend), is
 * the board's: the narrower reading is never assumed.
 */
export function isCommitteeOnlySeat(seat: Pick<OrgBoardMemberOut, 'scope'>): boolean {
  return seat.scope === 'committees';
}

/** The committees a seat serves on, as the read serves them (none when it serves on none). */
export function seatCommittees(
  seat: Pick<OrgBoardMemberOut, 'committees'>,
): { committee_id: string; name: string; role: string }[] {
  return (seat.committees ?? [])
    .filter((row) => typeof row.name === 'string' && row.name.length > 0)
    .map((row) => ({ committee_id: row.committee_id, name: row.name, role: row.role }));
}

/** Active seats first, then former seats; each group by display name, so the list is stable. */
export function sortSeats(seats: readonly OrgBoardMemberOut[]): OrgBoardMemberOut[] {
  return [...seats].sort((left, right) => {
    if (left.status !== right.status) return left.status === 'active' ? -1 : 1;
    return left.display_name.localeCompare(right.display_name);
  });
}

/** Badge tone for a seat status. PURELY VISUAL — the text comes from {@link statusLabel}. */
export function statusTone(status: string | null | undefined): BadgeTone {
  return (status ?? '').toLowerCase() === 'active' ? 'success' : 'neutral';
}

/** Human label for a seat status. */
export function statusLabel(status: string | null | undefined): string {
  switch ((status ?? '').toLowerCase()) {
    case 'active':
      return 'Serving';
    case 'removed':
      return 'Former';
    default:
      return humanize(status);
  }
}

/**
 * The service line under a seat's name: the dates the organization recorded, in its own terms.
 *
 * Prefers `service_history` (which the backend renders for both a module seat and an imported
 * historical one) and falls back to the seat's own term dates. Returns `null` rather than a
 * placeholder when the organization recorded nothing — an em dash here would suggest a value exists and is
 * missing, when in fact none was ever entered.
 */
export function serviceLine(seat: OrgBoardMemberOut): string | null {
  const history = seat.service_history ?? null;
  const from = history?.served_from ?? seat.term_start_on ?? null;
  const to = history?.served_to ?? seat.term_end_on ?? null;
  if (from === null && to === null) return null;
  if (from !== null && to !== null) return `${displayDate(from)} – ${displayDate(to)}`;
  if (from !== null) return `Since ${displayDate(from)}`;
  return `Until ${displayDate(to)}`;
}

/**
 * Whether this seat is one the organization entered as HISTORY rather than one the module watched. Worth
 * showing, because it changes what the recorded dates mean.
 */
export function isHistoricalSeat(seat: OrgBoardMemberOut): boolean {
  return seat.seat_provenance === 'imported' || seat.service_history?.is_historical === true;
}

/** Whether this seat has an account linked to it (which is what admits it to closed-session content). */
export function isLinked(seat: OrgBoardMemberOut): boolean {
  return seat.user_id !== null && seat.user_id !== undefined;
}

/**
 * A seat's connection to a Givmo account (CBG-SEC2, D-CBG-16/17; contract §5.1): a member joins the
 * board from an invitation into their own account, and an officer can see which stage each seat is
 * at. The roster's model declares `connection_status` and its siblings since the one regeneration
 * (SEC2-BE1b); an answer that carries none reads from the account link alone, and an unknown status
 * the same way.
 */
export type SeatConnection = 'not_invited' | 'invited' | 'joined';

export function seatConnection(seat: OrgBoardMemberOut): SeatConnection {
  switch (seat.connection_status) {
    case 'connected':
      return 'joined';
    case 'invited':
      return 'invited';
    case 'not_invited':
      return 'not_invited';
    default:
      return isLinked(seat) ? 'joined' : 'not_invited';
  }
}

/** The connection as the roster says it: one plain word (copy round 2, D-CBG-18). */
export function connectionLabel(connection: SeatConnection): string {
  switch (connection) {
    case 'joined':
      return 'Joined';
    case 'invited':
      return 'Invited';
    default:
      return 'Not invited';
  }
}

/**
 * The invite control's words: a seat already invited gets a NEW invitation (minting one replaces the
 * one before), so it says so rather than offering to invite someone who already has one (copy round
 * 2, D-CBG-18).
 */
export function inviteActionLabel(connection: SeatConnection): string {
  return connection === 'invited' ? 'Send a new invitation' : 'Invite to the board';
}

/** Until when the seat's newest invitation works, or null. */
export function invitationExpiresAt(seat: OrgBoardMemberOut): string | null {
  const value = seat.invitation_expires_at;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** The joined account's name (cosmetic: null when it can't be read). */
export function connectedAccountName(seat: OrgBoardMemberOut): string | null {
  const value = seat.connected_account_display_name;
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** Whether the joined account is also an administrator of the organization (detail only; null unknown). */
export function connectedAccountIsAdministrator(seat: OrgBoardMemberOut): boolean | null {
  const value = seat.connected_account_is_organization_admin;
  return typeof value === 'boolean' ? value : null;
}
